/**
 * Applicazione dell'apertura sorteggiata a una scacchiera.
 *
 * Le semi-mosse dell'apertura si giocano DAVVERO, con `applyMove`, prima che la
 * partita cominci: così cronologia, `halfMoveClock` e storico posizioni restano
 * coerenti con una partita normale, e la review può risalire dentro l'apertura.
 * È lo stesso schema di `GameRoom.applyDrawnOpening` lato server, qui in forma
 * pura perché serve anche al client (Sfida AI, che sorteggia in locale).
 *
 * `GameRoom` continua a usare la propria copia: applica le mosse attraverso il
 * proprio `applyMove` privato e tiene `forcedOpening` per il conteggio mosse e il
 * PDN. Convergere le due è un lavoro a sé.
 */

import {
  RANDOM_OPENING_PLIES,
  ballotCeiling,
  drawBallotOpening,
  isDrawnOpening,
  openingPlies,
  randomDrawnOpening,
  variantHasBallot,
  type BoardState,
  type DrawnOpening,
  type OpeningMode,
  type ParsedMove,
} from '@draughts/shared';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { applyMove } from './move-execution.js';

/** Esito dell'applicazione: la board post-apertura e cosa è stato giocato. */
export interface AppliedOpening {
  board: BoardState;
  /** `null` quando non è stata applicata alcuna apertura (mossa libera). */
  opening: DrawnOpening | null;
  plies: number;
}

/**
 * Gioca le semi-mosse indicate, riconoscendo ciascuna fra le mosse legali della
 * posizione. Si ferma alla prima che non trova: le tabelle federali sono verificate
 * (`openings-legality.test.ts`), quindi un buco significherebbe dati o variante
 * sbagliati, e proseguire darebbe una posizione arbitraria invece dell'apertura.
 */
export function applyParsedOpening(
  board: BoardState,
  parsed: readonly ParsedMove[],
): { board: BoardState; applied: ParsedMove[] } {
  let current = board;
  const applied: ParsedMove[] = [];

  for (const parsedMove of parsed) {
    const legal = getAllLegalMoves(current).find(
      (m) => m.from === parsedMove.from && m.to === parsedMove.to,
    );
    if (!legal) break;
    current = applyMove(current, legal);
    applied.push(parsedMove);
  }

  return { board: current, applied };
}

/**
 * Gioca `plies` semi-mosse legali a caso. `rng` è iniettabile perché i test non
 * debbano stubbare `Math.random`.
 */
export function applyRandomOpening(
  board: BoardState,
  plies: number,
  rng: () => number = Math.random,
): AppliedOpening {
  let current = board;
  const applied: ParsedMove[] = [];

  for (let i = 0; i < plies; i++) {
    const moves = getAllLegalMoves(current);
    if (moves.length === 0) break;
    const move = moves[Math.floor(rng() * moves.length)]!;
    current = applyMove(current, move);
    applied.push({ from: move.from, to: move.to });
  }

  if (applied.length === 0) return { board, opening: null, plies: 0 };
  return { board: current, opening: randomDrawnOpening(applied), plies: applied.length };
}

/**
 * Sorteggia e applica l'apertura del criterio scelto: ballot federale su italiana e
 * inglese, semi-mosse casuali altrove (e per i criteri `random*`). `free` è un no-op.
 *
 * Rispecchia `GameRoom.applyDrawnOpening`: un criterio `ballot*` chiesto su una
 * variante senza tabella ricade sulle semi-mosse casuali invece di fallire.
 */
export function drawAndApplyOpening(
  board: BoardState,
  opening: OpeningMode,
  rng: () => number = Math.random,
): AppliedOpening {
  if (!isDrawnOpening(opening)) return { board, opening: null, plies: 0 };

  const ceiling = ballotCeiling(opening);
  if (ceiling !== null && variantHasBallot(board.variant)) {
    const drawn = drawBallotOpening(board.variant, ceiling, rng);
    if (drawn) {
      const { board: after, applied } = applyParsedOpening(board, drawn.parsedMoves);
      if (applied.length === drawn.parsedMoves.length) {
        return { board: after, opening: drawn, plies: applied.length };
      }
    }
    // Pool vuoto o apertura non applicabile: meglio una partita con qualche
    // semi-mossa casuale che una posizione a metà dell'apertura.
    return applyRandomOpening(board, RANDOM_OPENING_PLIES, rng);
  }

  return applyRandomOpening(board, openingPlies(opening) || RANDOM_OPENING_PLIES, rng);
}
